import { motion } from "framer-motion";
import enGyanjivanDasjiSwami from "~/data/about-us/gyanjivanDasjiSwami.json";
import gujGyanjivanDasjiSwami from "~/data/about-us/gyanjivanDasjiSwami_gu.json";
import { useFontStyle } from "~/hook/useFontStyle";
import useIntersectionObserver from "~/hook/useIntersectionObserver";
import { useLanguageParams } from "~/hook/useLanguageParams";
import { cn } from "~/lib/utils";
import { BACKGROUND_IMAGE_STYLE } from "~/routes/$lang._index";

const ContentSection = ({
  title,
  content,
  imageSrc,
  imageAlt,
  index,
}: {
  title: string;
  content: string[];
  imageSrc: string;
  imageAlt: string;
  index: number;
}) => {
  const [ref, isVisible] = useIntersectionObserver();
  const { normalInter, medium } = useFontStyle();
  const isEvenIndex = index % 2 === 0;

  const containerVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { delay: 0.2, duration: 0.7, ease: "easeOut" },
    },
  };

  return (
    <motion.section ref={ref} initial="hidden" animate={isVisible ? "visible" : "hidden"} variants={containerVariants} className="mb-16">
      <div className={cn("grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center", isEvenIndex && "md:grid-flow-dense")}>
        {/* Image */}
        <motion.div
          variants={imageVariants}
          className={cn("overflow-hidden rounded-2xl shadow-xl border-2 border-primaryColor/20", isEvenIndex && "md:order-2")}
        >
          <motion.img transition={{ duration: 0.4 }} src={imageSrc} alt={imageAlt} className="w-full aspect-auto object-contain z-20" />
        </motion.div>

        {/* Content */}
        <motion.div
          variants={{
            hidden: { opacity: 0, x: isEvenIndex ? -30 : 30 },
            visible: {
              opacity: 1,
              x: 0,
              transition: { delay: 0.1, duration: 0.7, ease: "easeOut" },
            },
          }}
          className={cn("space-y-4", isEvenIndex && "md:order-1")}
        >
          {/* Title with icon background */}
          <div className="flex items-start gap-4">
            <h3 className={cn("text-2xl md:text-3xl font-bold text-textColor pt-2", medium)}>{title}</h3>
          </div>

          {/* Description */}
          {content.map((paragraph, index) => (
            <p
              key={index}
              className={cn("text-textSecondaryColor text-base paragraph-text-size 2xl:text-lg leading-relaxed text-justify", normalInter)}
            >
              {paragraph}
            </p>
          ))}

          {/* Decorative accent line */}
          <motion.div
            initial={{ scaleX: 0 }}
            animate={isVisible ? { scaleX: 1 } : { scaleX: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="origin-left w-24 h-1 bg-gradient-to-r from-primaryColor to-primaryColor/40 rounded-full mt-6"
          />
        </motion.div>
      </div>
    </motion.section>
  );
};

// Hero Section
const HeroSection = ({ gyanjivanDasjiSwamiData: whoweareData }: { gyanjivanDasjiSwamiData: typeof enGyanjivanDasjiSwami }) => {
  const [ref, isVisible] = useIntersectionObserver();
  const { medium } = useFontStyle();

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className="relative w-full mb-5"
    >
      <div className="w-full flex justify-center items-center px-4 md:px-6">
        <div className="w-full max-w-6xl text-center">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={isVisible ? { scale: 1, opacity: 1 } : { scale: 0.9, opacity: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <h1 className={cn("text-3xl md:text-4xl font-bold text-textColor mb-4", medium)}>{whoweareData.title}</h1>
            <div className="w-32 h-1 bg-gradient-to-r from-transparent via-primaryColor to-transparent mx-auto mb-6" />
            <p className={cn("text-lg md:text-xl text-textSecondaryColor italic max-w-2xl mx-auto", medium)}>{whoweareData.subTitle}</p>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

// Introduction Section
const IntroductionSection = ({ gyanjivanDasjiSwamiData: whoweareData }: { gyanjivanDasjiSwamiData: typeof enGyanjivanDasjiSwami }) => {
  const [ref, isVisible] = useIntersectionObserver();
  const { normalInter, medium } = useFontStyle();

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, x: -40 }}
      animate={isVisible ? { opacity: 1, x: 0 } : { opacity: 0, x: -40 }}
      transition={{ duration: 0.7, ease: "easeOut" }}
      className="mb-12 md:mb-20 px-4 md:px-6"
    >
      <div className="max-w-6xl mx-auto">
        <motion.div className="relative">
          {/* Decorative icon */}
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={isVisible ? { scale: 1, rotate: 0 } : { scale: 0, rotate: -180 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="absolute -top-6 left-6 md:left-12 w-12 h-12 bg-primaryColor rounded-full flex items-center justify-center shadow-lg z-10"
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
          </motion.div>

          <div className="p-8 md:p-12 rounded-2xl" style={BACKGROUND_IMAGE_STYLE}>
            {whoweareData.content.introduction.description.length > 0 &&
              whoweareData.content.introduction.description.map((desc, index) => (
                <p
                  key={index}
                  className={cn("text-textSecondaryColor text-base paragraph-text-size 2xl:text-lg leading-relaxed text-justify", normalInter)}
                >
                  {desc}
                </p>
              ))}
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
};

// Main Component
function GyanjivanDasjiSwami() {
  const lang = useLanguageParams();
  const gyanjivanDasjiSwamiData = lang === "gu" ? gujGyanjivanDasjiSwami : enGyanjivanDasjiSwami;
  const { normalInter } = useFontStyle();

  const pageVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={pageVariants}
      // className="w-full min-h-screen bg-gradient-to-br from-secondaryColor/20 via-white to-secondaryColor/10 "
    >
      {/* Hero Section */}
      <HeroSection gyanjivanDasjiSwamiData={gyanjivanDasjiSwamiData} />
      {/* Introduction Section */}
      <IntroductionSection gyanjivanDasjiSwamiData={gyanjivanDasjiSwamiData} />

      {/* Content Sections Container */}
      <motion.div className="max-w-6xl mx-auto px-4">
        {gyanjivanDasjiSwamiData.content.sections.map((section, index) => (
          <ContentSection
            key={index}
            title={section.title}
            content={section.content}
            imageSrc={section.imageSrc}
            imageAlt={section.imageAlt}
            index={index}
          />
        ))}
      </motion.div>
    </motion.div>
  );
}

export default GyanjivanDasjiSwami;
